package ai.wagemix.logic.auth

import ai.wagemix.data.touchesUser
import ai.wagemix.data.WMAuthException
import ai.wagemix.data.WMAuthSyncEvent
import ai.wagemix.logic.auth.WMAuthPresenter.Action
import ai.wagemix.logic.auth.WMAuthPresenter.Event
import ai.wagemix.logic.auth.WMAuthPresenter.Prompt
import ai.wagemix.logic.sendWMPasswordResetRequested
import ai.wagemix.logic.session
import ai.wagemix.logic.userData
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMPlanRules
import ai.wagemix.logic.WMRoutes
import ai.wagemix.net.models.WMAccountRules
import ai.wagemix.net.models.WMAuthFailure
import ai.wagemix.net.models.WMSession
import com.fsryan.foundation.data.FSSyncEvent
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.jvm.JvmInline
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.onEach

enum class WMEmailProblem { Blank, Invalid }
enum class WMPasswordProblem { Blank, TooShort, TooLong, NoDigit, NoUppercase, NoLowercase, Mismatch }
enum class WMNameProblem { Blank, TooLong }

/** Field-level validation of the auth forms; `null` means the field is fine. */
data class WMAuthFieldProblems(
    val firstName: WMNameProblem? = null,
    val lastName: WMNameProblem? = null,
    val email: WMEmailProblem? = null,
    val password: WMPasswordProblem? = null,
    val confirmPassword: WMPasswordProblem? = null
) {
    val isValid: Boolean get() = firstName == null && lastName == null && email == null && password == null && confirmPassword == null
}

/** Where a multi-step auth flow stands. */
enum class WMAuthStage { Idle, ResetLinkSent, ResetCodeVerified, PasswordChanged, VerificationApplied, VerificationSent }

/**
 * The auth forms: login, registration, forgot/reset password and the `/auth-action` landing.
 * Validation rules, typed failure reasons and the post-login routing live here.
 */
interface WMAuthPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        @ConsistentCopyVisibility
        data class Validate internal constructor(val firstName: String?, val lastName: String?, val email: String?, val password: String?, val confirmPassword: String?, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Validate> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class SignIn internal constructor(val email: String, val password: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SignIn> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class Register internal constructor(val firstName: String, val lastName: String, val email: String, val password: String, val confirmPassword: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Register> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class SendPasswordReset internal constructor(val email: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SendPasswordReset> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class VerifyResetCode internal constructor(val code: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<VerifyResetCode> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ConfirmPasswordReset internal constructor(val code: String, val password: String, val confirmPassword: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ConfirmPasswordReset> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ApplyActionCode internal constructor(val code: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ApplyActionCode> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class ResendVerification internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<ResendVerification> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createValidate(firstName: String?, lastName: String?, email: String?, password: String?, confirmPassword: String?, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSignIn(email: String, password: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createRegister(firstName: String, lastName: String, email: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSendPasswordReset(email: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createVerifyResetCode(code: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createConfirmPasswordReset(code: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createApplyActionCode(code: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createResendVerification(extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base = ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createValidate(firstName: String?, lastName: String?, email: String?, password: String?, confirmPassword: String?, extendedProperties: Map<String, Any>): Base = Validate(firstName, lastName, email, password, confirmPassword, extendedProperties)
                override fun createSignIn(email: String, password: String, extendedProperties: Map<String, Any>): Base = SignIn(email, password, extendedProperties)
                override fun createRegister(firstName: String, lastName: String, email: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any>): Base = Register(firstName, lastName, email, password, confirmPassword, extendedProperties)
                override fun createSendPasswordReset(email: String, extendedProperties: Map<String, Any>): Base = SendPasswordReset(email, extendedProperties)
                override fun createVerifyResetCode(code: String, extendedProperties: Map<String, Any>): Base = VerifyResetCode(code, extendedProperties)
                override fun createConfirmPasswordReset(code: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any>): Base = ConfirmPasswordReset(code, password, confirmPassword, extendedProperties)
                override fun createApplyActionCode(code: String, extendedProperties: Map<String, Any>): Base = ApplyActionCode(code, extendedProperties)
                override fun createResendVerification(extendedProperties: Map<String, Any>): Base = ResendVerification(extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        val fieldProblemsUpdate: WMAuthFieldProblems?
        val stageUpdate: WMAuthStage?
        /** The address a verified reset code belongs to. */
        val resetEmailUpdate: String?
        val navigationUpdate: WMNavigationRequest?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val fieldProblemsUpdate: WMAuthFieldProblems? = null,
            override val stageUpdate: WMAuthStage? = null,
            override val resetEmailUpdate: String? = null,
            override val navigationUpdate: WMNavigationRequest? = null
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createFieldProblems(problems: WMAuthFieldProblems): E
            fun createStage(stage: WMAuthStage): E
            fun createResetEmail(email: String): E
            fun createNavigation(request: WMNavigationRequest): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createFieldProblems(problems: WMAuthFieldProblems) = Base(base.none(), fieldProblemsUpdate = problems)
                override fun createStage(stage: WMAuthStage) = Base(base.none(), stageUpdate = stage)
                override fun createResetEmail(email: String) = Base(base.none(), resetEmailUpdate = email)
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        /** The backend refused; [reason] picks the message, [email] personalises it. */
        @ConsistentCopyVisibility
        data class AuthFailed internal constructor(val reason: WMAuthFailure, val email: String?): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
        }

        @ConsistentCopyVisibility
        data class EmailNotVerified internal constructor(val email: String): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
            enum class Selection { Dismiss, Resend }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun authFailed(reason: WMAuthFailure, email: String?): P
            fun emailNotVerified(email: String): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun authFailed(reason: WMAuthFailure, email: String?): Base = AuthFailed(reason, email)
                override fun emailNotVerified(email: String): Base = EmailNotVerified(email)
            }
        }
    }

    fun userEditedFields(firstName: String? = null, lastName: String? = null, email: String? = null, password: String? = null, confirmPassword: String? = null, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedSignIn(email: String, password: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedRegister(firstName: String, lastName: String, email: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedPasswordReset(email: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userArrivedWithResetCode(code: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedConfirmPasswordReset(code: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userArrivedWithVerificationCode(code: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedResendVerification(extendedProperties: Map<String, Any> = emptyMap())

    companion object {
        const val SCREEN = "auth"
        const val PARAM_PENDING_ROUTE = "pendingRoute"
        // The same rules the API enforces (WMAccountRules in models).
        const val MIN_PASSWORD_LENGTH = WMAccountRules.MIN_PASSWORD_LENGTH
        const val MAX_PASSWORD_LENGTH = WMAccountRules.MAX_PASSWORD_LENGTH
        const val MAX_NAME_LENGTH = WMAccountRules.MAX_NAME_LENGTH
        val EMAIL_PATTERN = WMAccountRules.EMAIL_PATTERN
    }
}

/** The validation rules the forms share (ported from the current site's input fields). */
object WMAuthRules {
    fun emailProblem(email: String): WMEmailProblem? = when {
        email.isBlank() -> WMEmailProblem.Blank
        !WMAuthPresenter.EMAIL_PATTERN.matches(email.trim()) -> WMEmailProblem.Invalid
        else -> null
    }

    fun passwordProblem(password: String): WMPasswordProblem? = when {
        password.isEmpty() -> WMPasswordProblem.Blank
        password.length < WMAuthPresenter.MIN_PASSWORD_LENGTH -> WMPasswordProblem.TooShort
        password.length > WMAuthPresenter.MAX_PASSWORD_LENGTH -> WMPasswordProblem.TooLong
        password.none { it.isDigit() } -> WMPasswordProblem.NoDigit
        password.none { it.isUpperCase() } -> WMPasswordProblem.NoUppercase
        password.none { it.isLowerCase() } -> WMPasswordProblem.NoLowercase
        else -> null
    }

    fun confirmProblem(password: String, confirm: String): WMPasswordProblem? = when {
        confirm.isEmpty() -> WMPasswordProblem.Blank
        confirm != password -> WMPasswordProblem.Mismatch
        else -> null
    }

    fun nameProblem(name: String): WMNameProblem? = when {
        name.isBlank() -> WMNameProblem.Blank
        name.trim().length > WMAuthPresenter.MAX_NAME_LENGTH -> WMNameProblem.TooLong
        else -> null
    }
}

abstract class WMBaseAuthPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMAuthDataGateway
>(
    /** "login" or "register": the workflow tag the post-login routes carry. */
    protected val workflow: String,
    /** The route to go to after sign-in when the page was asked for one (`?next=`). */
    protected val nextRoute: String?,
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory, dataGateway) { defaultAuthHandlePromptResponse(response, emit) }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMAuthPresenter<P, E, A> {

    override fun userEditedFields(firstName: String?, lastName: String?, email: String?, password: String?, confirmPassword: String?, extendedProperties: Map<String, Any>) =
        onUserAction(actionFactory.createValidate(firstName, lastName, email, password, confirmPassword, extendedProperties))
    override fun userRequestedSignIn(email: String, password: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSignIn(email, password, extendedProperties))
    override fun userRequestedRegister(firstName: String, lastName: String, email: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any>) =
        onUserAction(actionFactory.createRegister(firstName, lastName, email, password, confirmPassword, extendedProperties))
    override fun userRequestedPasswordReset(email: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSendPasswordReset(email, extendedProperties))
    override fun userArrivedWithResetCode(code: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createVerifyResetCode(code, extendedProperties))
    override fun userRequestedConfirmPasswordReset(code: String, password: String, confirmPassword: String, extendedProperties: Map<String, Any>) =
        onUserAction(actionFactory.createConfirmPasswordReset(code, password, confirmPassword, extendedProperties))
    override fun userArrivedWithVerificationCode(code: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createApplyActionCode(code, extendedProperties))
    override fun userRequestedResendVerification(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createResendVerification(extendedProperties))

    override fun registerSubscriptions() {
        // Post-login routing waits for the user document the auth sync mirrors into the users cache.
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .onEach {
                val user = dataGateway.retrieveActiveUser() ?: return@onEach
                val userData = user.userData ?: return@onEach
                val pending = dataGateway.retrieveUserContext(user.uid).screenParams(WMAuthPresenter.SCREEN)[WMAuthPresenter.PARAM_PENDING_ROUTE]
                    ?: return@onEach
                dataGateway.deleteScreenParams(user.uid, WMAuthPresenter.SCREEN)
                val route = nextRoute ?: WMPlanRules.postLoginRoute(userData.subscriptionType, userData.status, pending)
                eventFactory.createNavigation(WMNavigationRequest.Route(route)).emit()
            }
            .launchInViewScope()

        dataGateway.authSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { handleSyncEvent(it) }
            .launchInViewScope()
    }

    private suspend fun handleSyncEvent(event: WMAuthSyncEvent<*>) {
        when {
            event.initial -> eventFactory.createLoadingChange(true).emit()
            event is FSSyncEvent.Error<*> -> {
                val error = event.error
                stepKindOf(event)?.let { stage ->
                    val reason = (error as? WMAuthException)?.reason ?: WMAuthFailure.Network
                    FSLog.sendWMAuthFailed(stage = stage, reason = reason, error = error)
                }
                val prompt = when (error) {
                    is WMAuthException -> promptFactory.authFailed(error.reason, email = null)
                    else -> promptFactory.networkError()
                }
                eventFactory.createPromptChange(prompt, loading = false).emit()
            }
            else -> {
                eventFactory.createLoadingChange(false).emit()
                when (event) {
                    is WMAuthSyncEvent.SignIn.Success -> {
                        FSLog.sendWMLoginCompleted()
                        // The sync put the session into the users cache before it reported success.
                        dataGateway.session()?.let { routeAfterSignIn(it) }
                    }
                    is WMAuthSyncEvent.Register.Success -> {
                        FSLog.sendWMSignUpCompleted()
                        dataGateway.session()?.let { routeAfterSignIn(it) }
                    }
                    is WMAuthSyncEvent.SendPasswordReset.Success -> {
                        FSLog.sendWMPasswordResetRequested()
                        eventFactory.createStage(WMAuthStage.ResetLinkSent).emit()
                    }
                    is WMAuthSyncEvent.VerifyPasswordResetCode.Success -> {
                        dataGateway.retrieveGlobalContext().passwordResetEmail(event.code)?.let { eventFactory.createResetEmail(it).emit() }
                        eventFactory.createStage(WMAuthStage.ResetCodeVerified).emit()
                    }
                    is WMAuthSyncEvent.ConfirmPasswordReset.Success -> {
                        FSLog.sendWMPasswordResetCompleted()
                        eventFactory.createStage(WMAuthStage.PasswordChanged).emit()
                    }
                    is WMAuthSyncEvent.ApplyActionCode.Success -> {
                        FSLog.sendWMEmailVerified()
                        eventFactory.createStage(WMAuthStage.VerificationApplied).emit()
                    }
                    is WMAuthSyncEvent.SendEmailVerification.Success -> eventFactory.createStage(WMAuthStage.VerificationSent).emit()
                    else -> Unit
                }
            }
        }
    }

    /** Which auth step a sync belongs to, for `auth_failed`; sign-out and profile updates are not auth steps. */
    private fun stepKindOf(event: WMAuthSyncEvent<*>): WMAuthStepKind? = when (event) {
        is WMAuthSyncEvent.SignIn<*> -> WMAuthStepKind.Login
        is WMAuthSyncEvent.Register<*> -> WMAuthStepKind.Register
        is WMAuthSyncEvent.SendPasswordReset<*> -> WMAuthStepKind.Reset
        is WMAuthSyncEvent.VerifyPasswordResetCode<*> -> WMAuthStepKind.Reset
        is WMAuthSyncEvent.ConfirmPasswordReset<*> -> WMAuthStepKind.Reset
        is WMAuthSyncEvent.ApplyActionCode<*>, is WMAuthSyncEvent.SendEmailVerification<*> -> WMAuthStepKind.VerifyEmail
        else -> null
    }

    private suspend fun routeAfterSignIn(session: WMSession) {
        if (!session.emailVerified) {
            eventFactory.createPromptChange(promptFactory.emailNotVerified(session.email)).emit()
            return
        }
        when (val userData = dataGateway.userData()) {
            null -> dataGateway.upsertScreenParams(session.uid, WMAuthPresenter.SCREEN, mapOf(WMAuthPresenter.PARAM_PENDING_ROUTE to workflow))
            else -> {
                val route = nextRoute ?: WMPlanRules.postLoginRoute(userData.subscriptionType, userData.status, workflow)
                eventFactory.createNavigation(WMNavigationRequest.Route(route)).emit()
            }
        }
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        eventFactory.createStage(WMAuthStage.Idle).emit()
        eventFactory.createFieldProblems(WMAuthFieldProblems()).emit()
        // Already signed in and verified: skip the form.
        val session = dataGateway.session()
        if (isFirstOnReadyCall && session != null && session.emailVerified && workflow != "reset") {
            routeAfterSignIn(session)
        }
    }

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        when (action) {
            is Action.Validate -> launch { eventFactory.createFieldProblems(problemsOf(action.firstName, action.lastName, action.email, action.password, action.confirmPassword)).emit() }
            is Action.SignIn -> launch {
                val problems = WMAuthFieldProblems(email = WMAuthRules.emailProblem(action.email), password = if (action.password.isEmpty()) WMPasswordProblem.Blank else null)
                eventFactory.createFieldProblems(problems).emit()
                if (problems.isValid) {
                    dataGateway.signIn(action.email.trim(), action.password, createPresenterContext(action))
                }
            }
            is Action.Register -> launch {
                val problems = problemsOf(action.firstName, action.lastName, action.email, action.password, action.confirmPassword)
                eventFactory.createFieldProblems(problems).emit()
                if (problems.isValid) {
                    dataGateway.register(action.firstName.trim(), action.lastName.trim(), action.email.trim(), action.password, createPresenterContext(action))
                }
            }
            is Action.SendPasswordReset -> launch {
                val problems = WMAuthFieldProblems(email = WMAuthRules.emailProblem(action.email))
                eventFactory.createFieldProblems(problems).emit()
                if (problems.isValid) {
                    dataGateway.sendPasswordReset(action.email.trim(), createPresenterContext(action))
                }
            }
            is Action.VerifyResetCode -> dataGateway.verifyPasswordResetCode(action.code, createPresenterContext(action))
            is Action.ConfirmPasswordReset -> launch {
                val problems = WMAuthFieldProblems(
                    password = WMAuthRules.passwordProblem(action.password),
                    confirmPassword = WMAuthRules.confirmProblem(action.password, action.confirmPassword)
                )
                eventFactory.createFieldProblems(problems).emit()
                if (problems.isValid) {
                    dataGateway.confirmPasswordReset(action.code, action.password, createPresenterContext(action))
                }
            }
            is Action.ApplyActionCode -> dataGateway.applyActionCode(action.code, createPresenterContext(action))
            is Action.ResendVerification -> dataGateway.sendEmailVerification(createPresenterContext(action))
            else -> logUnknownActionType(action)
        }
    }

    /** Only the fields a form has (non-null) are judged. */
    protected open fun problemsOf(firstName: String?, lastName: String?, email: String?, password: String?, confirmPassword: String?) = WMAuthFieldProblems(
        firstName = firstName?.let { WMAuthRules.nameProblem(it) },
        lastName = lastName?.let { WMAuthRules.nameProblem(it) },
        email = email?.let { WMAuthRules.emailProblem(it) },
        password = password?.let { WMAuthRules.passwordProblem(it) },
        confirmPassword = confirmPassword?.let { WMAuthRules.confirmProblem(password ?: "", it) }
    )
}

context(eventFactory: EF, promptFactory: PF, dataGateway: G)
suspend fun <
    P: Prompt,
    E: Event<P>,
    EF: Event.Factory<P, E>,
    PF: Prompt.Factory<P>,
    G: WMAuthDataGateway
> defaultAuthHandlePromptResponse(response: FSPrompt.Response<P>, emit: suspend E.() -> Unit) {
    when (val prompt = response.prompt) {
        is Prompt.AuthFailed -> eventFactory.createPromptChange(promptFactory.none()).emit()
        is Prompt.EmailNotVerified -> {
            if (prompt.selectionOf(response.selected) == Prompt.EmailNotVerified.Selection.Resend) {
                eventFactory.createPromptChange(promptFactory.none(), loading = true).emit()
                dataGateway.sendEmailVerification()
            } else {
                eventFactory.createPromptChange(promptFactory.none()).emit()
            }
        }
        else -> defaultHandlePromptResponse(response, emit)
    }
}

typealias WMDefaultAuthPresenter = WMAuthPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMAuthPresenter.Companion.createDefault(
    workflow: String,
    nextRoute: String?,
    dataGateway: WMAuthDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMAuthPresenter")
): WMDefaultAuthPresenter = object: WMBaseAuthPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMAuthDataGateway
>(
    workflow = workflow,
    nextRoute = nextRoute,
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId
) {}
